/**
 * Curriculum Module - Full Page View
 * Displays learning paths for Python, T-SQL, and Prolog courses
 * Read-only view with progress tracking
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '@/services/api';
import { useModuleRefresh } from '@/contexts/ModuleRefreshContext';

interface Topic {
  id: number;
  code: string;
  name: string;
  module_name: string;
  module_order: number;
  topic_order: number;
  description: string;
  content?: string;
  key_concepts?: string;
  prerequisites?: string | string[];  // Can be JSON string or array
  estimated_minutes: number;
  difficulty: string;
}

interface Module {
  name: string;
  order: number;
  topics: Topic[];
}

interface Course {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  difficulty: string;
  estimated_hours: number;
  icon?: string;
  modules?: Module[];
}

interface CourseProgress {
  code: string;
  name: string;
  total_topics: number;
  completed: number;
  in_progress: number;
  not_started: number;
  progress_percent: number;
}

interface Enrollment {
  id: number;
  course_code: string;
  course_name: string;
  status: 'active' | 'paused' | 'completed' | 'dropped';
  enrolled_at: string;
  paused_at: string | null;
  resumed_at: string | null;
  last_activity_at: string | null;
  pause_reason: string | null;
  current_topic_code: string | null;
  progress_percent: number;
}

interface TopicProgress {
  code: string;
  name: string;
  module_name: string;
  status: string | null;
  completed_at: string | null;
  time_spent_minutes: number | null;
  notes: string | null;
}

const CurriculumModule: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { registerCurriculumRefresh } = useModuleRefresh();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [progress, setProgress] = useState<CourseProgress[]>([]);
  const [topicProgress, setTopicProgress] = useState<TopicProgress[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [view, setView] = useState<'courses' | 'course' | 'topic'>('courses');
  const [initialCourseLoaded, setInitialCourseLoaded] = useState(false);

  /**
   * Load courses on mount
   */
  useEffect(() => {
    loadCourses();
    loadProgress();
    loadEnrollments();
  }, []);

  /**
   * Handle URL course parameter - auto-open course if specified
   */
  useEffect(() => {
    const courseCode = searchParams.get('course');
    if (courseCode && !initialCourseLoaded) {
      setInitialCourseLoaded(true);
      // Clear the URL param after reading it
      setSearchParams({});
      // Load the specified course
      loadCourseDetails(courseCode);
    }
  }, [searchParams, initialCourseLoaded]);

  /**
   * Register refresh callback
   */
  useEffect(() => {
    if (registerCurriculumRefresh) {
      registerCurriculumRefresh(() => {
        loadCourses();
        loadProgress();
        loadEnrollments();
      });
    }
  }, [registerCurriculumRefresh]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/curriculum/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await apiClient.get('/curriculum/progress');
      // API returns { courses: [...] } for overall progress
      const data = response.data;
      if (data && data.courses && Array.isArray(data.courses)) {
        setProgress(data.courses);
      } else {
        setProgress([]);
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
      setProgress([]);
    }
  };

  const loadEnrollments = async () => {
    try {
      const response = await apiClient.get('/curriculum/enrollments');
      if (response.data && Array.isArray(response.data.enrollments)) {
        setEnrollments(response.data.enrollments);
      } else {
        setEnrollments([]);
      }
    } catch (error) {
      console.error('Failed to load enrollments:', error);
      setEnrollments([]);
    }
  };

  const enrollInCourse = async (courseCode: string) => {
    setActionLoading(courseCode);
    try {
      await apiClient.post(`/curriculum/enroll/${courseCode}`);
      await loadEnrollments();
    } catch (error) {
      console.error('Failed to enroll in course:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const pauseCourse = async (courseCode: string, reason?: string) => {
    setActionLoading(courseCode);
    try {
      await apiClient.post(`/curriculum/pause/${courseCode}`, { reason });
      await loadEnrollments();
    } catch (error) {
      console.error('Failed to pause course:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const resumeCourse = async (courseCode: string) => {
    setActionLoading(courseCode);
    try {
      await apiClient.post(`/curriculum/resume/${courseCode}`);
      await loadEnrollments();
    } catch (error) {
      console.error('Failed to resume course:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getEnrollmentStatus = (courseCode: string): Enrollment | undefined => {
    return enrollments.find(e => e.course_code === courseCode);
  };

  const loadCourseDetails = async (courseCode: string) => {
    setLoading(true);
    try {
      // Load course details and topic progress in parallel
      const [courseResponse, progressResponse] = await Promise.all([
        apiClient.get(`/curriculum/courses/${courseCode}`),
        apiClient.get(`/curriculum/progress?course_code=${courseCode}`)
      ]);
      setSelectedCourse(courseResponse.data);
      // Topic progress comes as { course_code, topics: [...] }
      const progressData = progressResponse.data;
      if (progressData && progressData.topics && Array.isArray(progressData.topics)) {
        setTopicProgress(progressData.topics);
      } else {
        setTopicProgress([]);
      }
      setView('course');
    } catch (error) {
      console.error('Failed to load course details:', error);
      setTopicProgress([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTopicContent = async (topicCode: string) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/curriculum/topics/${topicCode}`);
      setSelectedTopic(response.data);
      setView('topic');
    } catch (error) {
      console.error('Failed to load topic:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTopicStatus = (topicCode: string): string => {
    const p = topicProgress.find(pr => pr.code === topicCode);
    return p?.status || 'not_started';
  };

  const getTopicNotes = (topicCode: string): string | null => {
    const p = topicProgress.find(pr => pr.code === topicCode);
    return p?.notes || null;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
            Completed
          </span>
        );
      case 'in_progress':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
            In Progress
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            Not Started
          </span>
        );
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return 'text-green-600 bg-green-50';
      case 'intermediate':
        return 'text-yellow-600 bg-yellow-50';
      case 'advanced':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getEnrollmentBadge = (enrollment: Enrollment | undefined) => {
    if (!enrollment) return null;

    switch (enrollment.status) {
      case 'active':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
            Active
          </span>
        );
      case 'paused':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
            Paused
          </span>
        );
      case 'completed':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
            Completed
          </span>
        );
      default:
        return null;
    }
  };

  const getCourseIcon = (course: Course) => {
    // Use the icon from the API if available
    if (course.icon) {
      return <span className="text-3xl">{course.icon}</span>;
    }
    // Fallback to generic icon
    return (
      <svg className="w-8 h-8 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
      </svg>
    );
  };

  const getCourseProgress = (courseCode: string): number => {
    const courseProgress = progress.find(p => p.code === courseCode);
    return courseProgress?.progress_percent || 0;
  };

  // Helper to parse prerequisites (can be JSON string or array)
  const parsePrerequisites = (prereqs: string | string[] | undefined): string[] => {
    if (!prereqs) return [];
    if (Array.isArray(prereqs)) return prereqs;
    try {
      const parsed = JSON.parse(prereqs);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const handleBack = () => {
    if (view === 'topic') {
      setSelectedTopic(null);
      setView('course');
    } else if (view === 'course') {
      setSelectedCourse(null);
      setView('courses');
    } else {
      navigate('/voice');
    }
  };

  // Courses list view
  const renderCoursesView = () => (
    <>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Curriculum</h1>
                <p className="text-xs md:text-sm text-gray-500">{courses.length} courses available</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No courses available</h3>
            <p className="text-gray-500">Check back later for new courses</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((course) => {
              const progressPercent = getCourseProgress(course.code);
              const enrollment = getEnrollmentStatus(course.code);
              const isLoading = actionLoading === course.code;
              return (
                <div
                  key={course.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div
                    className="p-5 cursor-pointer"
                    onClick={() => loadCourseDetails(course.code)}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      {getCourseIcon(course)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{course.name}</h3>
                          {getEnrollmentBadge(enrollment)}
                        </div>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded ${getDifficultyColor(course.difficulty)}`}>
                          {course.difficulty}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{course.description}</p>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span>{course.estimated_hours} hours</span>
                      <span className="uppercase">{course.category}</span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">{progressPercent}% complete</p>
                  </div>

                  {/* Enrollment action buttons */}
                  <div className="px-5 pb-4 pt-0 flex gap-2">
                    {!enrollment && (
                      <button
                        onClick={(e) => { e.stopPropagation(); enrollInCourse(course.code); }}
                        disabled={isLoading}
                        className="flex-1 py-2 px-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Enroll
                          </>
                        )}
                      </button>
                    )}
                    {enrollment?.status === 'active' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); pauseCourse(course.code); }}
                        disabled={isLoading}
                        className="flex-1 py-2 px-3 text-sm font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-orange-700 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                            </svg>
                            Pause
                          </>
                        )}
                      </button>
                    )}
                    {enrollment?.status === 'paused' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); resumeCourse(course.code); }}
                        disabled={isLoading}
                        className="flex-1 py-2 px-3 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                            Resume
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => loadCourseDetails(course.code)}
                      className="py-2 px-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  // Course details view
  const renderCourseView = () => (
    <>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-3 flex-1">
              {selectedCourse && getCourseIcon(selectedCourse)}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{selectedCourse?.name}</h1>
                  {selectedCourse && getEnrollmentBadge(getEnrollmentStatus(selectedCourse.code))}
                </div>
                <p className="text-xs md:text-sm text-gray-500">
                  {selectedCourse?.modules?.reduce((acc, m) => acc + m.topics.length, 0) || 0} topics
                </p>
              </div>
              {/* Enrollment action in header */}
              {selectedCourse && (() => {
                const enrollment = getEnrollmentStatus(selectedCourse.code);
                const isLoading = actionLoading === selectedCourse.code;
                return (
                  <div className="flex gap-2">
                    {!enrollment && (
                      <button
                        onClick={() => enrollInCourse(selectedCourse.code)}
                        disabled={isLoading}
                        className="py-2 px-4 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Enroll
                          </>
                        )}
                      </button>
                    )}
                    {enrollment?.status === 'active' && (
                      <button
                        onClick={() => pauseCourse(selectedCourse.code)}
                        disabled={isLoading}
                        className="py-2 px-4 text-sm font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-orange-700 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                            </svg>
                            Pause
                          </>
                        )}
                      </button>
                    )}
                    {enrollment?.status === 'paused' && (
                      <button
                        onClick={() => resumeCourse(selectedCourse.code)}
                        disabled={isLoading}
                        className="py-2 px-4 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-green-700 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                            Resume
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Course info with progress */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <p className="text-gray-600 mb-4">{selectedCourse?.description}</p>
              <div className="flex flex-wrap gap-4 text-sm mb-4">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{selectedCourse?.estimated_hours} hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`px-2 py-0.5 rounded ${getDifficultyColor(selectedCourse?.difficulty || '')}`}>
                    {selectedCourse?.difficulty}
                  </span>
                </div>
              </div>

              {/* Progress summary */}
              {selectedCourse && (() => {
                const courseProgress = progress.find(p => p.code === selectedCourse.code);
                if (!courseProgress) return null;
                return (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Your Progress</span>
                      <span className="text-sm font-semibold text-blue-600">{courseProgress.progress_percent}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all"
                        style={{ width: `${courseProgress.progress_percent}%` }}
                      />
                    </div>
                    <div className="flex gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                        <span className="text-gray-600">{courseProgress.completed} completed</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                        <span className="text-gray-600">{courseProgress.in_progress} in progress</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-gray-300"></span>
                        <span className="text-gray-600">{courseProgress.not_started} not started</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modules and topics */}
            {selectedCourse?.modules?.map((module, moduleIndex) => (
              <div key={module.name} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">
                    Module {moduleIndex + 1}: {module.name}
                  </h3>
                  <p className="text-xs text-gray-500">{module.topics.length} topics</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {module.topics.map((topic) => {
                    const status = getTopicStatus(topic.code);
                    const notes = getTopicNotes(topic.code);
                    return (
                      <div
                        key={topic.id}
                        className="px-5 py-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => loadTopicContent(topic.code)}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 font-mono">{topic.code}</span>
                              <h4 className="font-medium text-gray-900 truncate">{topic.name}</h4>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 truncate">{topic.description}</p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs text-gray-400">{topic.estimated_minutes}min</span>
                            {getStatusBadge(status)}
                          </div>
                        </div>
                        {notes && (
                          <div className="mt-2 flex items-start gap-2 text-xs">
                            <span className="text-blue-500">📝</span>
                            <p className="text-blue-600 italic">{notes}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  // Topic content view
  const renderTopicView = () => (
    <>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <p className="text-xs text-gray-500">{selectedTopic?.module_name}</p>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{selectedTopic?.name}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : selectedTopic && (
          <div className="space-y-6">
            {/* Topic header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-mono text-gray-400">{selectedTopic.code}</span>
                {getStatusBadge(getTopicStatus(selectedTopic.code))}
              </div>
              <p className="text-gray-600 mb-4">{selectedTopic.description}</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{selectedTopic.estimated_minutes} minutes</span>
                </div>
                <span className={`px-2 py-0.5 rounded ${getDifficultyColor(selectedTopic.difficulty)}`}>
                  {selectedTopic.difficulty}
                </span>
              </div>

              {/* Tutor's notes */}
              {getTopicNotes(selectedTopic.code) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">📝</span>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Tutor's Notes</h4>
                      <p className="text-sm text-blue-600 italic">{getTopicNotes(selectedTopic.code)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Key concepts */}
            {selectedTopic.key_concepts && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Key Concepts</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedTopic.key_concepts.split(',').map((concept, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      {concept.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Prerequisites */}
            {(() => {
              const prereqs = parsePrerequisites(selectedTopic.prerequisites);
              return prereqs.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Prerequisites</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {prereqs.map((prereq, idx) => (
                      <li key={idx}>{prereq}</li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Content */}
            {selectedTopic.content && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Learning Content</h3>
                <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap">
                  {selectedTopic.content}
                </div>
              </div>
            )}

            {/* Call to action */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 p-5 text-center">
              <p className="text-gray-700 mb-3">
                Ready to learn this topic? Ask LAMED to teach you about <strong>{selectedTopic.name}</strong>!
              </p>
              <button
                onClick={() => navigate('/voice')}
                className="btn-primary inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                Start Learning
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {view === 'courses' && renderCoursesView()}
      {view === 'course' && renderCourseView()}
      {view === 'topic' && renderTopicView()}
    </div>
  );
};

export default CurriculumModule;
